import { useMemo } from "react";

import {
  FileExclamationOutlined,
  FileDoneOutlined,
  DollarOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { Column, Pie } from "@ant-design/plots";
import {
  Col,
  Row,
  Typography,
  Card,
  Statistic,
  Tooltip,
  DatePicker,
} from "antd";

import dayjs from "../../../config/dayjs";
import { formatCompactRM, formatRM } from "../../../utils/numberFormat";
import { styles } from "../styles/dashboardStyles";
import { Dashboard } from "../type/dashboard";

const { Text } = Typography;

interface AgentDashboardProps {
  dashboard: Dashboard;
  isSerembanBranch?: boolean;
  selectedYear?: number;
  selectedFileStatusDate?: string;
  onYearChange?: (year: number) => void;
  onFileStatusDateChange?: (date: string) => void;
}

const AgentDashboardCards = ({
  dashboard,
  isSerembanBranch,
  selectedYear,
  selectedFileStatusDate,
  onYearChange,
  onFileStatusDateChange,
}: AgentDashboardProps) => {
  const cardStyle = {
    ...styles.card,
    margin: "10px 0",
    height: 200,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    border: "1px solid #f0f0f0",
  };

  const monthKey = useMemo(() => {
    const base = selectedFileStatusDate
      ? dayjs(selectedFileStatusDate, "YYYY-MM")
      : dayjs();
    return base.format("MMM");
  }, [selectedFileStatusDate]);

  const pieData = useMemo(() => {
    const entry = dashboard?.fileCountsByMonth?.find(
      (d) => d.month === monthKey
    );
    const totalFiles = entry?.totalFiles ?? 0;
    const completedFiles = entry?.completedFiles ?? 0;
    const incompleteFiles = Math.max(totalFiles - completedFiles, 0);
    return [
      { type: "Completed", value: completedFiles },
      { type: "Incomplete", value: incompleteFiles },
    ];
  }, [dashboard, monthKey]);

  return (
    <>
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8} xl={8}>
            <Card
              // hoverable
              size="small"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <DollarOutlined
                    style={{
                      fontSize: "18px",
                      color: "#52c41a",
                      marginRight: "8px",
                    }}
                  />
                  <span>SPA/Loan/Finance/Tenancy/Contract Amount</span>
                </div>
              }
              style={cardStyle}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  padding: "16px",
                }}
              >
                <Statistic
                  value={dashboard?.totalSPAPrice || 0}
                  valueStyle={{
                    color: "#52c41a",
                    fontSize: "28px",
                    fontWeight: "bold",
                  }}
                  // prefix={<DollarOutlined />}
                  formatter={(value) => formatCompactRM(Number(value))}
                  valueRender={() => (
                    <Tooltip title={formatRM(dashboard?.totalSPAPrice || 0)}>
                      {formatCompactRM(dashboard?.totalSPAPrice || 0)}
                    </Tooltip>
                  )}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: "14px",
                    marginTop: "8px",
                    textAlign: "center",
                  }}
                >
                  Total SPA price
                </Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={8}>
            <Card
              //   hoverable
              size="small"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <FileDoneOutlined
                    style={{
                      fontSize: "18px",
                      color: "#52c41a",
                      marginRight: "8px",
                    }}
                  />
                  <span>Completed Files</span>
                </div>
              }
              style={cardStyle}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  padding: "16px",
                }}
              >
                <Statistic
                  value={dashboard?.totalOwnFileCompleted || 0}
                  valueStyle={{
                    color: "#52c41a",
                    fontSize: "28px",
                    fontWeight: "bold",
                  }}
                  prefix={<FileDoneOutlined />}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: "14px",
                    marginTop: "8px",
                    textAlign: "center",
                  }}
                >
                  Successfully completed files
                </Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={8}>
            <Card
              //   hoverable
              size="small"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <FileExclamationOutlined
                    style={{
                      fontSize: "18px",
                      color: "#ff4d4f",
                      marginRight: "8px",
                    }}
                  />
                  <span>All Incomplete Files</span>
                </div>
              }
              style={cardStyle}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  padding: "16px",
                }}
              >
                <Statistic
                  value={dashboard?.totalOwnFileNotCompleted || 0}
                  valueStyle={{
                    color: "#ff4d4f",
                    fontSize: "28px",
                    fontWeight: "bold",
                  }}
                  prefix={<FileExclamationOutlined />}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: "14px",
                    marginTop: "8px",
                    textAlign: "center",
                  }}
                >
                  Files pending completion
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </Col>

      {/* check only show if user is in seremban branch */}
      {isSerembanBranch && (
        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} lg={24} xl={12}>
              <Card
                size="small"
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "0 0 16px 0",
                    }}
                  >
                    <span>SPA Price Overview</span>
                    <DatePicker
                      picker="year"
                      value={dayjs(`${selectedYear}-01-01`)}
                      onChange={(date) =>
                        onYearChange &&
                        onYearChange(date ? date.year() : dayjs().year())
                      }
                      allowClear={false}
                      format="YYYY"
                      style={{ width: 120, borderRadius: "6px" }}
                      suffixIcon={
                        <CalendarOutlined style={{ color: "#1890ff" }} />
                      }
                    />
                  </div>
                }
                style={{
                  ...styles.card,
                  margin: "10px 0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  minHeight: 400,
                }}
              >
                <Column
                  data={dashboard?.spaPriceInYear || []}
                  xField="month"
                  yField="total"
                  columnWidthRatio={0.5}
                  color="#52c41a"
                  xAxis={{
                    title: { text: "Month" },
                    label: { autoRotate: true, autoHide: true },
                  }}
                  yAxis={{
                    title: { text: "SPA Price (RM)" },
                    label: {
                      formatter: (v) => formatCompactRM(Number(v)),
                    },
                    nice: true,
                  }}
                  tooltip={{
                    formatter: (d) => ({
                      name: "SPA Price",
                      value: `RM ${Number(d.total).toLocaleString()}`,
                    }),
                  }}
                  autoFit
                  height={320}
                  label={false}
                  meta={{
                    month: { alias: "Month" },
                    total: { alias: "SPA Price" },
                  }}
                  interactions={[{ type: "element-active" }]}
                  padding={[24, 24, 48, 72]}
                  appendPadding={8}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} lg={16} xl={12}>
              <Card
                size="small"
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "0 0 16px 0",
                    }}
                  >
                    <span>Files by Status</span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <DatePicker
                        picker="month"
                        value={
                          selectedFileStatusDate
                            ? dayjs(selectedFileStatusDate, "YYYY-MM")
                            : dayjs()
                        }
                        onChange={(date) =>
                          onFileStatusDateChange &&
                          onFileStatusDateChange(
                            date
                              ? date.format("YYYY-MM")
                              : dayjs().format("YYYY-MM")
                          )
                        }
                        allowClear={false}
                        format="MMM YYYY"
                        style={{ width: 120, borderRadius: "6px" }}
                        suffixIcon={
                          <CalendarOutlined style={{ color: "#1890ff" }} />
                        }
                      />
                    </div>
                  </div>
                }
                style={{
                  ...styles.card,
                  margin: "10px 0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  minHeight: 400,
                }}
              >
                <Pie
                  data={pieData}
                  angleField="value"
                  colorField="type"
                  radius={0.9}
                  label={{
                    type: "inner",
                    content: ({ percent }) => `${(percent * 100).toFixed(0)}%`,
                  }}
                  legend={{ position: "bottom" }}
                  tooltip={{
                    formatter: (d) => ({ name: d.type, value: d.value }),
                  }}
                  color={["#52c41a", "#ff4d4f"]}
                  interactions={[{ type: "element-active" }]}
                  statistic={{
                    title: false,
                    content: {
                      content: `${monthKey}`,
                    },
                  }}
                  height={320}
                  padding={[24, 24, 48, 48]}
                  appendPadding={8}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      )}
    </>
  );
};

export default AgentDashboardCards;
